import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
   getExamTicketById,
   getExamTicketQuestions,
   getExamTickets,
   updateExamQuestion,
   updateExamTicket,
   buildExamTicketsExportBundle,
   downloadJsonExport,
} from "../../api/examAdminService";
import {
   buildImportApplicationPlan,
   parseOfficialTicketsImport,
   resolveImportTicket,
} from "../../utils/examTicketsOfficialImport";
import {
   getQuestionCategories,
   getQuestionCategoriesWithCount,
} from "../../api/questionCategoriesService";
import { rewriteImageUrl } from "../Utils/rewriteImageUrl";

const TICKETS_PAGE_LIMIT = 100;
const MIN_ANSWERS = 2;
const MAX_ANSWERS = 6;
const NO_IMAGE_PLACEHOLDER_URL =
   "https://auto-test.online/static/custom/img/noimage.jpg";
const EMPTY_VERIFICATION_SUMMARY = {
   ticketsScanned: 0,
   questionsScanned: 0,
   issuesCount: 0,
   ticketsWithIssues: 0,
   questionsWithIssues: 0,
   failedTickets: 0,
};

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toPositiveInt(value) {
   const n = Number(value);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function uniqPositiveInts(values) {
   return Array.from(
      new Set(
         safeList(values)
            .map((item) => Number(item))
            .filter((item) => Number.isInteger(item) && item > 0),
      ),
   );
}

function firstNonEmpty(...values) {
   for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
   }
   return "";
}

function truncateText(value, max = 120) {
   const text = String(value ?? "").trim();
   if (text.length <= max) return text;
   return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function foldText(input) {
   const text = String(input ?? "");
   return text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
}

function normalizeExamImagePath(raw) {
   const src = String(raw || "").trim();
   if (!src) return null;

   const noHost = src.replace(/^https?:\/\/[^/]+/i, "");
   const withSlash = noHost.startsWith("/") ? noHost : `/${noHost}`;
   const withoutLegacyPrefix = withSlash.replace(
      /^\/EXAMEN_INSTRUIRE_AUTO(?=\/)/i,
      "",
   );
   const compact = withoutLegacyPrefix.replace(/\/{2,}/g, "/");
   if (!compact) return null;

   const withImagesPrefix = /^\/images\//i.test(compact)
      ? compact
      : `/images${compact.startsWith("/") ? compact : `/${compact}`}`;

   return withImagesPrefix.replace(
      /\/image(\d+)\.(?:jpe?g|png|webp)$/i,
      "/Image$1.jpeg",
   );
}

function normalizeLooseKey(value) {
   return String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
}

function getValueByLooseKeys(obj, keys = []) {
   if (!isObject(obj)) return undefined;
   const normalizedKeys = keys.map((key) => normalizeLooseKey(key));

   for (const [key, value] of Object.entries(obj)) {
      if (normalizedKeys.includes(normalizeLooseKey(key))) {
         return value;
      }
   }

   return undefined;
}

function parseJsonArraySafe(value, fallback = []) {
   if (Array.isArray(value)) return value;
   if (typeof value !== "string") return fallback;

   const text = String(value ?? "").trim();
   if (!text) return fallback;

   try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : fallback;
   } catch {
      return fallback;
   }
}

function parseLocalizedTextRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO, value?.textRo, value?.text_ro),
         ru: firstNonEmpty(value?.ru, value?.RU, value?.textRu, value?.text_ru),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedTextRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

function parseLocalizedAnswerRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO),
         ru: firstNonEmpty(value?.ru, value?.RU),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedAnswerRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

function stringifyLocalizedAnswer(answer) {
   const ro = String(answer?.ro ?? "").trim();
   const ru = String(answer?.ru ?? "").trim();

   return JSON.stringify({
      ro: ro || ru,
      ru: ru || ro,
   });
}

function readItems(raw, fallbackKeys = []) {
   if (Array.isArray(raw)) return raw;
   if (!isObject(raw)) return [];

   const keys = [
      "data",
      "items",
      "results",
      "rows",
      "tickets",
      "questions",
      ...fallbackKeys,
   ];

   for (const key of keys) {
      const value = raw?.[key];
      if (Array.isArray(value)) return value;
   }

   const nestedData = raw?.data;
   if (isObject(nestedData)) {
      for (const key of keys) {
         const value = nestedData?.[key];
         if (Array.isArray(value)) return value;
      }
   }

   return [];
}

function unwrapDataObject(raw) {
   if (!isObject(raw)) return raw;
   if (isObject(raw?.data) && !Array.isArray(raw?.data)) return raw.data;
   return raw;
}

function inferCorrectAnswerFromAnswerFlags(answersBase = []) {
   for (let i = 0; i < answersBase.length; i += 1) {
      const answer = answersBase[i];
      if (!isObject(answer)) continue;

      const flag =
         answer.correct ??
         answer.isCorrect ??
         answer.right ??
         answer.ok ??
         answer.valid ??
         false;

      if (flag === true) return i + 1;
   }

   return null;
}

function normalizeCorrectAnswer(rawValue, answersCount) {
   const text = String(rawValue ?? "").trim();
   if (!text) return null;

   let n = null;

   if (/^[+-]?\d+$/.test(text)) {
      n = Number(text);
   } else {
      const upper = text.toUpperCase();

      if (/^[A-F]$/.test(upper)) {
         n = upper.charCodeAt(0) - 64;
      } else {
         const romanMap = {
            I: 1,
            II: 2,
            III: 3,
            IV: 4,
            V: 5,
            VI: 6,
         };

         if (Object.prototype.hasOwnProperty.call(romanMap, upper)) {
            n = romanMap[upper];
         }
      }
   }

   if (!Number.isInteger(n) || answersCount <= 0) return null;
   if (n >= 1 && n <= answersCount) return n;
   if (n >= 0 && n < answersCount) return n + 1;
   return null;
}

function readFallbackAnswerSeries(raw, builders = []) {
   const values = [];
   let hasAtLeastOne = false;

   for (let index = 1; index <= MAX_ANSWERS; index += 1) {
      let picked;

      for (const buildKey of builders) {
         const key = buildKey(index);
         if (raw?.[key] != null) {
            picked = raw[key];
            break;
         }
      }

      if (picked != null) hasAtLeastOne = true;
      values.push(picked);
   }

   return hasAtLeastOne ? values : [];
}

function normalizeTicketSummary(item, fallbackIndex = 0) {
   if (!isObject(item)) return null;

   const id = toPositiveInt(item?.id);
   if (!id) return null;

   const name = String(item?.name ?? `Bilet ${fallbackIndex + 1}`).trim();
   const type =
      String(item?.type ?? "EXAM")
         .trim()
         .toUpperCase() || "EXAM";

   const questionCount =
      toPositiveInt(
         item?._count?.questions ?? item?.questionCount ?? item?.questionsCount,
      ) ?? uniqPositiveInts(item?.questionIds).length;

   return {
      id,
      localId: `ticket-${id}`,
      name,
      type,
      questionCount,
   };
}

function normalizeTicketList(items) {
   return safeList(items)
      .map((item, index) => normalizeTicketSummary(item, index))
      .filter(Boolean)
      .sort((a, b) => Number(a.id) - Number(b.id));
}

function readQuestionIds(raw, fallbackQuestions = []) {
   const source = unwrapDataObject(raw);

   const directIds = uniqPositiveInts(
      parseJsonArraySafe(source?.questionIds, safeList(source?.questionIds)),
   );
   if (directIds.length) return directIds;

   const nestedIds = uniqPositiveInts(
      safeList(source?.questions).map((question) => question?.id ?? question?.questionId),
   );
   if (nestedIds.length) return nestedIds;

   return uniqPositiveInts(safeList(fallbackQuestions).map((question) => question?.id));
}

function normalizeExamQuestion(raw, index = 0) {
   if (!isObject(raw)) return null;

   const id = toPositiveInt(raw?.id ?? raw?.questionId);
   if (!id) return null;

   const textLocalized = parseLocalizedTextRaw(
      raw?.text ?? raw?.question ?? raw?.title ?? raw?.content ?? raw?.questionText,
   );

   const textRo = firstNonEmpty(
      raw?.textRo,
      raw?.text_ro,
      raw?.textRO,
      textLocalized.ro,
   );
   const textRu = firstNonEmpty(
      raw?.textRu,
      raw?.text_ru,
      raw?.textRU,
      textLocalized.ru,
   );

   const answersBase = parseJsonArraySafe(
      raw?.answers,
      readFallbackAnswerSeries(raw, [
         (i) => `a${i}`,
         (i) => `answer${i}`,
      ]),
   );
   const answersRuBase = parseJsonArraySafe(
      raw?.answersRu ?? raw?.answersRU ?? raw?.answers_ru,
      readFallbackAnswerSeries(raw, [
         (i) => `a${i}Ru`,
         (i) => `a${i}RU`,
         (i) => `a${i}_ru`,
         (i) => `answer${i}Ru`,
         (i) => `answer${i}RU`,
         (i) => `answer${i}_ru`,
      ]),
   );

   const answers = [];
   const sourceLen = Math.max(answersBase.length, answersRuBase.length);

   for (let idx = 0; idx < sourceLen; idx += 1) {
      const roValue = parseLocalizedAnswerRaw(answersBase[idx]);
      const ruValue = parseLocalizedAnswerRaw(answersRuBase[idx]);

      const ro = firstNonEmpty(roValue?.ro, roValue?.ru);
      const ru = firstNonEmpty(ruValue?.ru, ruValue?.ro, roValue?.ru);

      if (!ro && !ru) continue;

      answers.push({
         ro: ro || ru,
         ru: ru || ro,
      });
   }

   const rawAnswerCount = answers.length;

   while (answers.length < MIN_ANSWERS) {
      answers.push({ ro: "", ru: "" });
   }

   const correctAnswerRaw =
      getValueByLooseKeys(raw, [
         "correctAnswer",
         "correct_answer",
         "correctIndex",
         "correct_index",
         "rightIndex",
         "right_index",
         "answerIndex",
         "answer_index",
         "rightAnswer",
         "right_answer",
         "goodAnswer",
         "good_answer",
         "solution",
         "answer",
      ]) ?? inferCorrectAnswerFromAnswerFlags(answersBase);

   const parsedCorrectAnswer = normalizeCorrectAnswer(
      correctAnswerRaw,
      answers.length,
   );
   const correctAnswer = parsedCorrectAnswer || 1;

   const order = toPositiveInt(raw?.order) ?? index + 1;
   const image = firstNonEmpty(raw?.image, raw?.img);
   const categoryId =
      raw?.categoryId == null ? null : (toPositiveInt(raw?.categoryId) ?? null);

   return {
      id,
      order,
      textRo,
      textRu,
      image,
      answers,
      correctAnswer,
      categoryId,
      rawAnswerCount,
      correctAnswerRaw,
      correctAnswerFallbackUsed: !parsedCorrectAnswer,
   };
}

function normalizeTicketQuestions(raw) {
   return safeList(readItems(raw, ["questions"]))
      .map((question, index) => normalizeExamQuestion(question, index))
      .filter(Boolean)
      .sort(
         (left, right) =>
            Number(left?.order ?? 0) - Number(right?.order ?? 0) ||
            Number(left?.id ?? 0) - Number(right?.id ?? 0),
      );
}

function orderQuestionsByIds(questions, questionIds = []) {
   const ids = uniqPositiveInts(questionIds);
   if (!ids.length) return safeList(questions);

   const weights = new Map(ids.map((id, index) => [Number(id), index]));
   return [...safeList(questions)].sort((left, right) => {
      const leftWeight = weights.has(Number(left?.id))
         ? weights.get(Number(left?.id))
         : Number.MAX_SAFE_INTEGER;
      const rightWeight = weights.has(Number(right?.id))
         ? weights.get(Number(right?.id))
         : Number.MAX_SAFE_INTEGER;

      if (leftWeight !== rightWeight) return leftWeight - rightWeight;
      return Number(left?.order ?? 0) - Number(right?.order ?? 0);
   });
}

function normalizeTicketDetail(raw, fallbackSummary, normalizedQuestions) {
   const source = unwrapDataObject(raw);
   const id = toPositiveInt(source?.id ?? fallbackSummary?.id);
   if (!id) return null;

   return {
      id,
      name:
         String(source?.name ?? fallbackSummary?.name ?? `Bilet ${id}`).trim() ||
         `Bilet ${id}`,
      type:
         String(source?.type ?? fallbackSummary?.type ?? "EXAM")
            .trim()
            .toUpperCase() || "EXAM",
      questionIds: readQuestionIds(source, normalizedQuestions),
   };
}

function cloneAnswers(answers) {
   return safeList(answers).map((answer) => ({
      ro: String(answer?.ro ?? ""),
      ru: String(answer?.ru ?? ""),
   }));
}

function pickDraftLangText(valueRo, valueRu, lang = "ro") {
   if (lang === "ru") {
      return firstNonEmpty(valueRu, valueRo);
   }

   return firstNonEmpty(valueRo, valueRu);
}

function buildExamPreviewQuestion(draft, lang = "ro") {
   if (!draft) return null;

   const answers = cloneAnswers(draft?.answers).map((answer) =>
      pickDraftLangText(answer?.ro, answer?.ru, lang) || "(necompletat)",
   );
   const correctAnswer =
      normalizeCorrectAnswer(draft?.correctAnswer, answers.length) || null;

   return {
      text:
         pickDraftLangText(draft?.textRo, draft?.textRu, lang) ||
         "(întrebare necompletată)",
      image: normalizeExamImagePath(draft?.image),
      answers,
      correctIndex: Number.isInteger(correctAnswer) ? correctAnswer - 1 : null,
  };
}

function buildGlobalQuestionSearchEntry(ticket, question, fallbackOrder = 1) {
   const ticketId = toPositiveInt(ticket?.id);
   const questionId = toPositiveInt(question?.id);
   if (!ticketId || !questionId) return null;

   const order =
      toPositiveInt(question?.order) ?? toPositiveInt(fallbackOrder) ?? 1;
   const answers = cloneAnswers(question?.answers);
   const ticketName =
      String(ticket?.name ?? `Bilet ${ticketId}`).trim() || `Bilet ${ticketId}`;
   const textRo = String(question?.textRo ?? "");
   const textRu = String(question?.textRu ?? "");

   return {
      localId: `search-${ticketId}-${questionId}`,
      ticketId,
      ticketName,
      questionId,
      order,
      textRo,
      textRu,
      categoryId:
         question?.categoryId == null ? null : Number(question?.categoryId),
      image: String(question?.image ?? ""),
      searchable: foldText(
         [
            ticketId,
            ticketName,
            questionId,
            order,
            textRo,
            textRu,
            ...answers.flatMap((answer) => [answer?.ro, answer?.ru]),
         ].join(" "),
      ),
   };
}

function sortGlobalSearchEntries(entries) {
   return [...safeList(entries)].sort(
      (left, right) =>
         Number(left?.ticketId ?? 0) - Number(right?.ticketId ?? 0) ||
         Number(left?.order ?? 0) - Number(right?.order ?? 0) ||
         Number(left?.questionId ?? 0) - Number(right?.questionId ?? 0),
   );
}

function createVerificationSummary(overrides = {}) {
   return {
      ...EMPTY_VERIFICATION_SUMMARY,
      ...overrides,
   };
}

function buildVerificationFinding({
   ticket,
   question = null,
   code,
   shortLabel,
   message,
}) {
   const ticketId = toPositiveInt(ticket?.id);
   if (!ticketId) return null;

   const questionId = toPositiveInt(question?.id);
   const order = toPositiveInt(question?.order);
   const ticketName =
      String(ticket?.name ?? `Bilet ${ticketId}`).trim() || `Bilet ${ticketId}`;
   const previewText = firstNonEmpty(question?.textRo, question?.textRu);
   const slug = foldText(`${code} ${shortLabel} ${message}`).replace(/\s+/g, "-");

   return {
      localId: `verification-${ticketId}-${questionId || "ticket"}-${
         order || "na"
      }-${slug || code || "issue"}`,
      ticketId,
      ticketName,
      questionId,
      order,
      code: String(code ?? "").trim(),
      shortLabel: String(shortLabel ?? "").trim() || "Problemă",
      message: String(message ?? "").trim() || "Date incomplete.",
      previewText,
   };
}

function buildQuestionVerificationFindings(ticket, question) {
   if (!question?.id) return [];

   const findings = [];
   const textRo = String(question?.textRo ?? "").trim();
   const textRu = String(question?.textRu ?? "").trim();

   if (!textRo && !textRu) {
      findings.push(
         buildVerificationFinding({
            ticket,
            question,
            code: "question-text-missing-both",
            shortLabel: "Text RO + RU lipsă",
            message: "Lipsește textul întrebării atât în română, cât și în rusă.",
         }),
      );
   } else {
      if (!textRo) {
         findings.push(
            buildVerificationFinding({
               ticket,
               question,
               code: "question-text-missing-ro",
               shortLabel: "Text RO lipsă",
               message: "Lipsește textul întrebării în limba română.",
            }),
         );
      }

      if (!textRu) {
         findings.push(
            buildVerificationFinding({
               ticket,
               question,
               code: "question-text-missing-ru",
               shortLabel: "Text RU lipsă",
               message: "Lipsește textul întrebării în limba rusă.",
            }),
         );
      }
   }

   const answers = cloneAnswers(question?.answers);
   const rawAnswerCount = Math.max(
      0,
      Number.isFinite(Number(question?.rawAnswerCount))
         ? Number(question.rawAnswerCount)
         : answers.length,
   );

   if (rawAnswerCount < MIN_ANSWERS) {
      findings.push(
         buildVerificationFinding({
            ticket,
            question,
            code: "answers-count-too-low",
            shortLabel: "Prea puține răspunsuri",
            message: `Întrebarea are doar ${rawAnswerCount} răspunsuri definite.`,
         }),
      );
   }

   const answerSlots = Math.max(MIN_ANSWERS, answers.length, rawAnswerCount);
   for (let answerIndex = 0; answerIndex < answerSlots; answerIndex += 1) {
      const answer = answers[answerIndex] || { ro: "", ru: "" };
      const answerRo = String(answer?.ro ?? "").trim();
      const answerRu = String(answer?.ru ?? "").trim();
      const answerNo = answerIndex + 1;

      if (!answerRo && !answerRu) {
         findings.push(
            buildVerificationFinding({
               ticket,
               question,
               code: `answer-${answerNo}-missing-both`,
               shortLabel: `Răspuns ${answerNo} lipsă`,
               message: `Lipsește complet răspunsul ${answerNo} (RO și RU).`,
            }),
         );
         continue;
      }

      if (!answerRo) {
         findings.push(
            buildVerificationFinding({
               ticket,
               question,
               code: `answer-${answerNo}-missing-ro`,
               shortLabel: `Răspuns ${answerNo} RO lipsă`,
               message: `Lipsește răspunsul ${answerNo} în limba română.`,
            }),
         );
      }

      if (!answerRu) {
         findings.push(
            buildVerificationFinding({
               ticket,
               question,
               code: `answer-${answerNo}-missing-ru`,
               shortLabel: `Răspuns ${answerNo} RU lipsă`,
               message: `Lipsește răspunsul ${answerNo} în limba rusă.`,
            }),
         );
      }
   }

   if (question?.correctAnswerFallbackUsed) {
      findings.push(
         buildVerificationFinding({
            ticket,
            question,
            code: "correct-answer-missing",
            shortLabel: "Răspuns corect lipsă",
            message: "Nu există un răspuns corect valid setat pentru întrebare.",
         }),
      );
   } else {
      const correctAnswer = normalizeCorrectAnswer(
         question?.correctAnswer,
         answers.length,
      );
      const correctOption = Number.isInteger(correctAnswer)
         ? answers[correctAnswer - 1]
         : null;
      const correctOptionRo = String(correctOption?.ro ?? "").trim();
      const correctOptionRu = String(correctOption?.ru ?? "").trim();

      if (!correctOption || (!correctOptionRo && !correctOptionRu)) {
         findings.push(
            buildVerificationFinding({
               ticket,
               question,
               code: "correct-answer-empty-option",
               shortLabel: "Răspuns corect gol",
               message:
                  "Răspunsul marcat ca fiind corect indică o opțiune fără text.",
            }),
         );
      }
   }

   return findings.filter(Boolean);
}

function buildTicketVerificationFindings(ticket, questions, loadError = null) {
   if (loadError) {
      return [
         buildVerificationFinding({
            ticket,
            code: "ticket-load-failed",
            shortLabel: "Bilet neverificat",
            message: `Nu am putut încărca întrebările biletului: ${
               loadError?.message || "eroare necunoscută"
            }.`,
         }),
      ].filter(Boolean);
   }

   const normalizedQuestions = safeList(questions);
   const findings = [];

   if (!normalizedQuestions.length) {
      findings.push(
         buildVerificationFinding({
            ticket,
            code: "ticket-empty",
            shortLabel: "Bilet fără întrebări",
            message: "Biletul nu conține întrebări încărcate.",
         }),
      );
   }

   for (const question of normalizedQuestions) {
      findings.push(...buildQuestionVerificationFindings(ticket, question));
   }

   return findings.filter(Boolean);
}

function sortVerificationFindings(findings) {
   return [...safeList(findings)].sort(
      (left, right) =>
         Number(left?.ticketId ?? 0) - Number(right?.ticketId ?? 0) ||
         Number(left?.order ?? Number.MAX_SAFE_INTEGER) -
            Number(right?.order ?? Number.MAX_SAFE_INTEGER) ||
         Number(left?.questionId ?? Number.MAX_SAFE_INTEGER) -
            Number(right?.questionId ?? Number.MAX_SAFE_INTEGER) ||
         String(left?.shortLabel ?? "").localeCompare(String(right?.shortLabel ?? "")) ||
         String(left?.message ?? "").localeCompare(String(right?.message ?? "")),
   );
}

function summarizeVerificationFindings(findings) {
   const questionIssueKeys = new Set();
   const ticketIssueIds = new Set();

   for (const finding of safeList(findings)) {
      const ticketId = toPositiveInt(finding?.ticketId);
      const questionId = toPositiveInt(finding?.questionId);

      if (ticketId) {
         ticketIssueIds.add(ticketId);
      }

      if (ticketId && questionId) {
         questionIssueKeys.add(`${ticketId}:${questionId}`);
      }
   }

   return {
      ticketsWithIssues: ticketIssueIds.size,
      questionsWithIssues: questionIssueKeys.size,
   };
}

function createDraftFromQuestion(question) {
   if (!question) return null;

   return {
      id: question.id,
      textRo: String(question?.textRo ?? ""),
      textRu: String(question?.textRu ?? ""),
      image: String(question?.image ?? ""),
      answers: cloneAnswers(question?.answers),
      correctAnswer:
         normalizeCorrectAnswer(question?.correctAnswer, safeList(question?.answers).length) ||
         1,
      categoryId:
         question?.categoryId == null ? "" : String(question?.categoryId),
   };
}

function toComparableQuestionShape(source) {
   return {
      textRo: String(source?.textRo ?? "").trim(),
      textRu: String(source?.textRu ?? "").trim(),
      image: String(source?.image ?? "").trim(),
      correctAnswer: Number(source?.correctAnswer ?? 1),
      categoryId:
         source?.categoryId == null || String(source?.categoryId) === ""
            ? null
            : Number(source?.categoryId),
      answers: cloneAnswers(source?.answers).map((answer) => ({
         ro: String(answer?.ro ?? "").trim(),
         ru: String(answer?.ru ?? "").trim(),
      })),
   };
}

function isQuestionDirty(question, draft) {
   if (!question || !draft) return false;
   return (
      JSON.stringify(toComparableQuestionShape(question)) !==
      JSON.stringify(toComparableQuestionShape(draft))
   );
}

function buildQuestionPayloadFromDraft(draft) {
   const textRo = String(draft?.textRo ?? "").trim();
   const textRu = String(draft?.textRu ?? "").trim();

   if (!textRo && !textRu) {
      throw new Error("Întrebarea trebuie să aibă text în cel puțin o limbă.");
   }

   const answers = cloneAnswers(draft?.answers).map((answer) => ({
      ro: String(answer?.ro ?? "").trim(),
      ru: String(answer?.ru ?? "").trim(),
   }));

   if (answers.length < MIN_ANSWERS) {
      throw new Error(`Sunt necesare minimum ${MIN_ANSWERS} răspunsuri.`);
   }

   if (answers.some((answer) => !answer.ro && !answer.ru)) {
      throw new Error("Toate răspunsurile trebuie completate înainte de salvare.");
   }

   const correctAnswer = Number(draft?.correctAnswer);
   if (!Number.isInteger(correctAnswer) || correctAnswer < 1 || correctAnswer > answers.length) {
      throw new Error("Selectează răspunsul corect.");
   }

   const categoryText = String(draft?.categoryId ?? "").trim();
   const categoryId = !categoryText ? null : toPositiveInt(categoryText);
   if (categoryText && categoryId == null) {
      throw new Error("Categoria selectată este invalidă.");
   }

   return {
      text: JSON.stringify({
         ro: textRo,
         ru: textRu,
      }),
      answers: answers.map((answer) => stringifyLocalizedAnswer(answer)),
      correctAnswer,
      categoryId,
      image: String(draft?.image ?? "").trim(),
   };
}

function applyDraftToQuestion(question, draft) {
   if (!question || !draft) return question;

   return {
      ...question,
      textRo: String(draft?.textRo ?? ""),
      textRu: String(draft?.textRu ?? ""),
      image: String(draft?.image ?? ""),
      answers: cloneAnswers(draft?.answers),
      correctAnswer: Number(draft?.correctAnswer ?? 1),
      categoryId:
         draft?.categoryId == null || String(draft?.categoryId) === ""
            ? null
            : Number(draft?.categoryId),
   };
}

function normalizeCategoryList(raw) {
   if (Array.isArray(raw)) return raw;

   const items =
      raw?.data ||
      raw?.items ||
      raw?.results ||
      raw?.rows ||
      raw?.categories ||
      [];

   return Array.isArray(items) ? items : [];
}

async function loadAllExamTickets() {
   const collected = [];
   let page = 1;
   let guard = 0;

   while (guard < 500) {
      const response = await getExamTickets({
         page,
         limit: TICKETS_PAGE_LIMIT,
         type: "EXAM",
      });

      const rows = normalizeTicketList(readItems(response, ["tickets"]));
      collected.push(...rows);

      const totalPages = Number(response?.pagination?.totalPages);
      if (Number.isInteger(totalPages) && totalPages > 0) {
         if (page >= totalPages) break;
      } else if (rows.length < TICKETS_PAGE_LIMIT) {
         break;
      }

      page += 1;
      guard += 1;
   }

   const byId = new Map();

   for (const row of collected) {
      if (!row?.id || byId.has(row.id)) continue;
      byId.set(row.id, row);
   }

   return Array.from(byId.values()).sort((left, right) => Number(left.id) - Number(right.id));
}

export default function ExamCorrectionAdmin() {
   const [alert, setAlert] = useState(null);

   const [ticketRows, setTicketRows] = useState([]);
   const [ticketLoading, setTicketLoading] = useState(false);
   const [ticketSearch, setTicketSearch] = useState("");
   const [selectedTicketId, setSelectedTicketId] = useState(null);

   const [selectedTicketLoading, setSelectedTicketLoading] = useState(false);
   const [selectedTicketMeta, setSelectedTicketMeta] = useState(null);
   const [ticketDraftName, setTicketDraftName] = useState("");
   const [ticketOriginalQuestionIds, setTicketOriginalQuestionIds] = useState([]);

   const [questionRows, setQuestionRows] = useState([]);
   const [selectedQuestionId, setSelectedQuestionId] = useState(null);
   const [questionDrafts, setQuestionDrafts] = useState({});

   const [ticketSaving, setTicketSaving] = useState(false);
   const [questionSaving, setQuestionSaving] = useState(false);
   const [previewLang, setPreviewLang] = useState("ro");
   const [globalQuestionSearch, setGlobalQuestionSearch] = useState("");
   const [globalSearchEntries, setGlobalSearchEntries] = useState([]);
   const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
   const [globalSearchReady, setGlobalSearchReady] = useState(false);
   const [globalSearchError, setGlobalSearchError] = useState("");
   const [verificationLoading, setVerificationLoading] = useState(false);
   const [verificationReady, setVerificationReady] = useState(false);
   const [verificationStale, setVerificationStale] = useState(false);
   const [verificationFindings, setVerificationFindings] = useState([]);
   const [verificationSummary, setVerificationSummary] = useState(() =>
      createVerificationSummary(),
   );
   const [verificationError, setVerificationError] = useState("");

   const [exportLoading, setExportLoading] = useState(false);
   const [exportProgress, setExportProgress] = useState(null);

   const [importFileName, setImportFileName] = useState("");
   const [importBundle, setImportBundle] = useState(null);
   const [importApplying, setImportApplying] = useState(false);

   const [categories, setCategories] = useState([]);

   const ticketRowsRef = useRef([]);
   const selectedTicketLoadRef = useRef(0);
   const globalSearchLoadRef = useRef(0);
   const verificationLoadRef = useRef(0);
   const pendingQuestionJumpRef = useRef(null);

   useEffect(() => {
      ticketRowsRef.current = ticketRows;
   }, [ticketRows]);

   const pushAlert = useCallback((message, type = "info") => {
      setAlert({
         id: Date.now() + Math.random(),
         type,
         message: String(message ?? ""),
      });
   }, []);

   const resetVerificationReport = useCallback(() => {
      setVerificationReady(false);
      setVerificationStale(false);
      setVerificationFindings([]);
      setVerificationSummary(createVerificationSummary());
      setVerificationError("");
   }, []);

   useEffect(() => {
      if (!alert) return undefined;
      const timer = window.setTimeout(() => setAlert(null), 6500);
      return () => window.clearTimeout(timer);
   }, [alert]);

   const loadTickets = useCallback(async () => {
      try {
         setTicketLoading(true);
         setGlobalSearchReady(false);
         setGlobalSearchEntries([]);
         setGlobalSearchError("");
         resetVerificationReport();
         const rows = await loadAllExamTickets();
         setTicketRows(rows);

         if (!rows.length) {
            setSelectedTicketId(null);
            return;
         }

         setSelectedTicketId((current) => {
            const exists = rows.some((row) => Number(row.id) === Number(current));
            return exists ? current : rows[0].id;
         });
      } catch (error) {
         pushAlert(
            `Nu am putut încărca biletele: ${error?.message || "eroare"}`,
            "error",
         );
      } finally {
         setTicketLoading(false);
      }
   }, [pushAlert, resetVerificationReport]);

   const handleExportAllTickets = useCallback(async () => {
      if (exportLoading) return;

      setExportLoading(true);
      setExportProgress({ current: 0, total: 0, ticketName: "Pornire…" });

      try {
         const bundle = await buildExamTicketsExportBundle({
            ticketType: "EXAM",
            onProgress: (progress) => setExportProgress(progress),
         });

         const stamp = new Date().toISOString().slice(0, 10);
         downloadJsonExport(
            bundle,
            `bilete-exam-export-${stamp}.json`,
         );

         pushAlert(
            `Export reușit: ${bundle.summary.ticketsTotal} bilete, ${bundle.summary.questionsTotal} întrebări.`,
            "success",
         );
      } catch (error) {
         pushAlert(
            error?.message || "Nu am putut exporta biletele.",
            "error",
         );
      } finally {
         setExportLoading(false);
         setExportProgress(null);
      }
   }, [exportLoading, pushAlert]);

   const handleImportOfficialFile = useCallback(
      async (event) => {
         const file = event.target.files?.[0];
         event.target.value = "";
         if (!file) return;

         try {
            const text = String(await file.text()).replace(/^\uFEFF/, "");
            let parsed = JSON.parse(text);
            if (!parsed || typeof parsed !== "object") {
               throw new Error("Fișier JSON invalid.");
            }

            const normalized = parseOfficialTicketsImport(parsed);
            if (!normalized.tickets.length) {
               throw new Error(
                  normalized.errors[0] ||
                     "Fișierul nu conține bilete cu întrebări.",
               );
            }

            setImportFileName(file.name);
            setImportBundle(normalized);

            const warning =
               normalized.summary.correctFallbackCount > 0
                  ? ` · ${normalized.summary.correctFallbackCount} fără răspuns corect explicit`
                  : "";

            pushAlert(
               `Import: ${normalized.summary.ticketsTotal} bilete, ${normalized.summary.questionsTotal} întrebări${warning}.`,
               normalized.errors.length ||
                  normalized.summary.correctFallbackCount
                  ? "warning"
                  : "success",
            );
         } catch (error) {
            setImportFileName("");
            setImportBundle(null);
            pushAlert(
               error?.message || "Nu am putut citi fișierul JSON.",
               "error",
            );
         }
      },
      [pushAlert],
   );

   const loadCategories = useCallback(async () => {
      try {
         try {
            const raw = await getQuestionCategoriesWithCount();
            setCategories(normalizeCategoryList(raw));
         } catch {
            const raw = await getQuestionCategories(1, 1000);
            setCategories(normalizeCategoryList(raw));
         }
      } catch (error) {
         pushAlert(
            `Nu am putut încărca categoriile: ${error?.message || "eroare"}`,
            "warning",
         );
         setCategories([]);
      }
   }, [pushAlert]);

   const loadSelectedTicket = useCallback(
      async (ticketId) => {
         const resolvedId = toPositiveInt(ticketId);
         if (!resolvedId) {
            setSelectedTicketMeta(null);
            setTicketDraftName("");
            setTicketOriginalQuestionIds([]);
            setQuestionRows([]);
            setSelectedQuestionId(null);
            setQuestionDrafts({});
            return;
         }

         const requestId = selectedTicketLoadRef.current + 1;
         selectedTicketLoadRef.current = requestId;

         try {
            setSelectedTicketLoading(true);

            const fallbackSummary =
               ticketRowsRef.current.find(
                  (ticket) => Number(ticket.id) === Number(resolvedId),
               ) || null;

            const [ticketRaw, questionsRaw] = await Promise.all([
               getExamTicketById(resolvedId),
               getExamTicketQuestions(resolvedId),
            ]);

            if (selectedTicketLoadRef.current !== requestId) return;

            const normalizedQuestions = normalizeTicketQuestions(questionsRaw);

            const ticketMeta = normalizeTicketDetail(
               ticketRaw,
               fallbackSummary,
               normalizedQuestions,
            );

            if (!ticketMeta) {
               throw new Error("Biletul selectat are date invalide.");
            }

            const orderedQuestions = orderQuestionsByIds(
               normalizedQuestions,
               ticketMeta.questionIds,
            );
            const currentQuestionIds = orderedQuestions.map((question) => question.id);
            const pendingJump = pendingQuestionJumpRef.current;

            setSelectedTicketMeta({
               ...ticketMeta,
               questionIds: currentQuestionIds,
            });
            setTicketDraftName(ticketMeta.name);
            setTicketOriginalQuestionIds(currentQuestionIds);
            setQuestionRows(orderedQuestions);
            setQuestionDrafts({});
            setSelectedQuestionId((current) => {
               const desiredQuestionId =
                  pendingJump?.ticketId === resolvedId
                     ? pendingJump.questionId
                     : current;
               const hasDesired = orderedQuestions.some(
                  (question) => Number(question.id) === Number(desiredQuestionId),
               );
               if (hasDesired) return desiredQuestionId;

               const exists = orderedQuestions.some(
                  (question) => Number(question.id) === Number(current),
               );
               return exists ? current : (orderedQuestions[0]?.id ?? null);
            });

            if (pendingJump?.ticketId === resolvedId) {
               pendingQuestionJumpRef.current = null;
            }

            setTicketRows((current) =>
               current.map((ticket) =>
                  Number(ticket.id) === Number(ticketMeta.id)
                     ? {
                          ...ticket,
                          name: ticketMeta.name,
                          questionCount: orderedQuestions.length,
                       }
                     : ticket,
               ),
            );
         } catch (error) {
            if (selectedTicketLoadRef.current !== requestId) return;
            pushAlert(
               `Nu am putut încărca biletul selectat: ${error?.message || "eroare"}`,
               "error",
            );
         } finally {
            if (selectedTicketLoadRef.current === requestId) {
               setSelectedTicketLoading(false);
            }
         }
      },
      [pushAlert],
   );

   const handleApplyOfficialImport = useCallback(async () => {
      if (!importBundle?.tickets?.length || importApplying) return;

      const dbTickets = ticketRowsRef.current;
      if (!dbTickets.length) {
         pushAlert("Încarcă mai întâi biletele din backend.", "warning");
         return;
      }

      const ok = window.confirm(
         `Aplici importul pentru ${importBundle.tickets.length} bilete și ${importBundle.summary.questionsTotal} întrebări?\n\nSe actualizează textele RO/RU, răspunsurile și răspunsul corect.`,
      );
      if (!ok) return;

      setImportApplying(true);
      const log = [];
      let appliedQuestions = 0;

      try {
         for (const importTicket of importBundle.tickets) {
            let dbTicket = resolveImportTicket(importTicket, dbTickets);

            if (!dbTicket && importTicket.ticketKey === "selected") {
               dbTicket =
                  dbTickets.find(
                     (row) => Number(row.id) === Number(selectedTicketId),
                  ) || null;
            }

            if (!dbTicket?.id) {
               log.push({
                  status: "error",
                  target: importTicket.ticketKey,
                  message: "Biletul nu a fost găsit în backend.",
               });
               continue;
            }

            const [ticketRaw, questionsRaw] = await Promise.all([
               getExamTicketById(dbTicket.id),
               getExamTicketQuestions(dbTicket.id),
            ]);

            const existingQuestions = normalizeTicketQuestions(questionsRaw);
            const ticketMeta = normalizeTicketDetail(
               ticketRaw,
               dbTicket,
               existingQuestions,
            );
            const plan = buildImportApplicationPlan(
               importTicket,
               dbTicket,
               existingQuestions,
               ticketMeta?.questionIds || [],
            );

            let ticketApplied = 0;
            const readyUpdates = plan.updates.filter(
               (row) => row.status === "ready",
            );

            for (const update of readyUpdates) {
               await updateExamQuestion(update.questionId, update.payload);
               ticketApplied += 1;
            }

            const nextQuestionIds = readyUpdates
               .sort((left, right) => Number(left.order) - Number(right.order))
               .map((row) => Number(row.questionId))
               .filter((id) => Number.isInteger(id) && id > 0);

            const currentIds = safeList(ticketMeta?.questionIds).map((id) =>
               Number(id),
            );

            if (
               nextQuestionIds.length &&
               JSON.stringify(nextQuestionIds) !== JSON.stringify(currentIds)
            ) {
               await updateExamTicket(dbTicket.id, {
                  name:
                     String(ticketMeta?.name ?? dbTicket?.name ?? "").trim() ||
                     `Ticket ${dbTicket.id}`,
                  questionIds: nextQuestionIds,
               });
            }

            appliedQuestions += ticketApplied;

            if (plan.missingCount) {
               log.push({
                  status: "warning",
                  target: `#${dbTicket.id} · ${plan.ticketName}`,
                  message: `${ticketApplied} actualizate, ${plan.missingCount} lipsă.`,
               });
            } else {
               log.push({
                  status: "ok",
                  target: `#${dbTicket.id} · ${plan.ticketName}`,
                  message: `${ticketApplied} întrebări actualizate.`,
               });
            }

            if (Number(selectedTicketId) === Number(dbTicket.id)) {
               await loadSelectedTicket(dbTicket.id);
            }
         }

         await loadTickets();

         const failed = log.filter((row) => row.status === "error").length;
         if (failed) {
            pushAlert(
               `Import parțial: ${appliedQuestions} întrebări, ${failed} bilete negăsite.`,
               "warning",
            );
         } else {
            pushAlert(
               `Import aplicat: ${appliedQuestions} întrebări actualizate.`,
               "success",
            );
         }
      } catch (error) {
         pushAlert(
            error?.message || "Nu am putut aplica importul.",
            "error",
         );
      } finally {
         setImportApplying(false);
      }
   }, [
      importApplying,
      importBundle,
      loadSelectedTicket,
      loadTickets,
      pushAlert,
      selectedTicketId,
   ]);

   const ensureGlobalSearchIndex = useCallback(async (forceReload = false) => {
      if (globalSearchLoading) return;
      if (globalSearchReady && !forceReload) return;

      const tickets = ticketRowsRef.current;
      if (!tickets.length) return;

      const requestId = globalSearchLoadRef.current + 1;
      globalSearchLoadRef.current = requestId;

      try {
         setGlobalSearchLoading(true);
         setGlobalSearchError("");

         const settled = await Promise.allSettled(
            tickets.map(async (ticket) => {
               const questionsRaw = await getExamTicketQuestions(ticket.id);
               const normalizedQuestions = normalizeTicketQuestions(questionsRaw);

               return normalizedQuestions
                  .map((question, index) =>
                     buildGlobalQuestionSearchEntry(ticket, question, index + 1),
                  )
                  .filter(Boolean);
            }),
         );

         if (globalSearchLoadRef.current !== requestId) return;

         const entries = [];
         const failedTicketIds = [];

         for (let index = 0; index < settled.length; index += 1) {
            const result = settled[index];
            const ticket = tickets[index];

            if (result.status === "fulfilled") {
               entries.push(...safeList(result.value));
            } else if (ticket?.id) {
               failedTicketIds.push(ticket.id);
            }
         }

         setGlobalSearchEntries(sortGlobalSearchEntries(entries));
         setGlobalSearchReady(true);

         if (failedTicketIds.length) {
            pushAlert(
               `Indexarea căutării a omis ${failedTicketIds.length} bilete.`,
               "warning",
            );
         }
      } catch (error) {
         if (globalSearchLoadRef.current !== requestId) return;
         setGlobalSearchEntries([]);
         setGlobalSearchReady(false);
         setGlobalSearchError(
            error?.message || "Nu am putut indexa întrebările pentru căutare.",
         );
      } finally {
         if (globalSearchLoadRef.current === requestId) {
            setGlobalSearchLoading(false);
         }
      }
   }, [globalSearchLoading, globalSearchReady, pushAlert]);

   const runVerificationScan = useCallback(async () => {
      if (verificationLoading || globalSearchLoading) return;

      const tickets = ticketRowsRef.current;
      if (!tickets.length) return;

      const requestId = verificationLoadRef.current + 1;
      verificationLoadRef.current = requestId;

      try {
         setVerificationLoading(true);
         setVerificationError("");
         setVerificationStale(false);

         const settled = await Promise.allSettled(
            tickets.map(async (ticket) => {
               const questionsRaw = await getExamTicketQuestions(ticket.id);
               const normalizedQuestions = normalizeTicketQuestions(questionsRaw);

               return {
                  ticket,
                  questions: normalizedQuestions,
                  entries: normalizedQuestions
                     .map((question, index) =>
                        buildGlobalQuestionSearchEntry(ticket, question, index + 1),
                     )
                     .filter(Boolean),
                  findings: buildTicketVerificationFindings(
                     ticket,
                     normalizedQuestions,
                  ),
               };
            }),
         );

         if (verificationLoadRef.current !== requestId) return;

         const searchEntries = [];
         const findings = [];
         let questionsScanned = 0;
         let failedTickets = 0;

         for (let index = 0; index < settled.length; index += 1) {
            const result = settled[index];
            const ticket = tickets[index];

            if (result.status === "fulfilled") {
               searchEntries.push(...safeList(result.value?.entries));
               findings.push(...safeList(result.value?.findings));
               questionsScanned += safeList(result.value?.questions).length;
               continue;
            }

            failedTickets += 1;
            findings.push(
               ...buildTicketVerificationFindings(ticket, [], result.reason),
            );
         }

         const sortedEntries = sortGlobalSearchEntries(searchEntries);
         const sortedFindings = sortVerificationFindings(findings);
         const { ticketsWithIssues, questionsWithIssues } =
            summarizeVerificationFindings(sortedFindings);

         setGlobalSearchEntries(sortedEntries);
         setGlobalSearchReady(true);
         setGlobalSearchError("");
         setVerificationFindings(sortedFindings);
         setVerificationSummary(
            createVerificationSummary({
               ticketsScanned: tickets.length,
               questionsScanned,
               issuesCount: sortedFindings.length,
               ticketsWithIssues,
               questionsWithIssues,
               failedTickets,
            }),
         );
         setVerificationReady(true);

         if (!sortedFindings.length) {
            pushAlert(
               `Verificare completă: ${tickets.length} bilete, fără probleme detectate.`,
               "success",
            );
         } else if (failedTickets) {
            pushAlert(
               `Verificare completă cu probleme: ${sortedFindings.length} lipsuri găsite, ${failedTickets} bilete nu au putut fi verificate complet.`,
               "warning",
            );
         } else {
            pushAlert(
               `Verificare completă: ${sortedFindings.length} lipsuri găsite în ${ticketsWithIssues} bilete.`,
               "warning",
            );
         }
      } catch (error) {
         if (verificationLoadRef.current !== requestId) return;
         setVerificationFindings([]);
         setVerificationSummary(createVerificationSummary());
         setVerificationReady(false);
         setVerificationError(
            error?.message || "Nu am putut verifica toate biletele.",
         );
      } finally {
         if (verificationLoadRef.current === requestId) {
            setVerificationLoading(false);
         }
      }
   }, [globalSearchLoading, pushAlert, verificationLoading]);

   const runSelectedTicketVerification = useCallback(async () => {
      if (
         verificationLoading ||
         globalSearchLoading ||
         selectedTicketLoading ||
         ticketSaving ||
         questionSaving
      ) {
         return;
      }

      const ticketId = toPositiveInt(selectedTicketMeta?.id ?? selectedTicketId);
      if (!ticketId) return;

      const ticket =
         selectedTicketMeta ||
         ticketRowsRef.current.find((row) => Number(row.id) === Number(ticketId));

      if (!ticket) {
         pushAlert("Nu am găsit biletul selectat pentru verificare.", "error");
         return;
      }

      const requestId = verificationLoadRef.current + 1;
      verificationLoadRef.current = requestId;

      try {
         setVerificationLoading(true);
         setVerificationError("");
         setVerificationStale(false);

         const questionsRaw = await getExamTicketQuestions(ticketId);
         if (verificationLoadRef.current !== requestId) return;

         const normalizedQuestions = normalizeTicketQuestions(questionsRaw);
         const sortedFindings = sortVerificationFindings(
            buildTicketVerificationFindings(ticket, normalizedQuestions),
         );
         const { ticketsWithIssues, questionsWithIssues } =
            summarizeVerificationFindings(sortedFindings);

         setVerificationFindings(sortedFindings);
         setVerificationSummary(
            createVerificationSummary({
               ticketsScanned: 1,
               questionsScanned: normalizedQuestions.length,
               issuesCount: sortedFindings.length,
               ticketsWithIssues,
               questionsWithIssues,
               failedTickets: 0,
            }),
         );
         setVerificationReady(true);

         if (!sortedFindings.length) {
            pushAlert(
               `Biletul ${ticket.name || `#${ticketId}`} a fost verificat. Nu am găsit probleme.`,
               "success",
            );
         } else {
            pushAlert(
               `Biletul ${ticket.name || `#${ticketId}`} are ${sortedFindings.length} probleme de completat.`,
               "warning",
            );
         }
      } catch (error) {
         if (verificationLoadRef.current !== requestId) return;
         setVerificationFindings([]);
         setVerificationSummary(createVerificationSummary());
         setVerificationReady(false);
         setVerificationError(
            error?.message || "Nu am putut verifica biletul selectat.",
         );
      } finally {
         if (verificationLoadRef.current === requestId) {
            setVerificationLoading(false);
         }
      }
   }, [
      globalSearchLoading,
      pushAlert,
      questionSaving,
      selectedTicketId,
      selectedTicketLoading,
      selectedTicketMeta,
      ticketSaving,
      verificationLoading,
   ]);

   useEffect(() => {
      loadTickets();
      loadCategories();
   }, [loadCategories, loadTickets]);

   useEffect(() => {
      loadSelectedTicket(selectedTicketId);
   }, [loadSelectedTicket, selectedTicketId]);

   useEffect(() => {
      const query = String(globalQuestionSearch ?? "").trim();
      if (!query) return;
      if (!ticketRows.length) return;
      if (globalSearchReady || globalSearchLoading) return;
      ensureGlobalSearchIndex();
   }, [
      ensureGlobalSearchIndex,
      globalQuestionSearch,
      globalSearchLoading,
      globalSearchReady,
      ticketRows.length,
   ]);

   const filteredTickets = useMemo(() => {
      const query = String(ticketSearch ?? "").trim().toLowerCase();
      if (!query) return ticketRows;

      return ticketRows.filter((ticket) => {
         const haystack = `${ticket.id} ${ticket.name} ${ticket.type}`.toLowerCase();
         return haystack.includes(query);
      });
   }, [ticketRows, ticketSearch]);

   const normalizedGlobalSearch = useMemo(
      () => foldText(globalQuestionSearch),
      [globalQuestionSearch],
   );

   const globalSearchResults = useMemo(() => {
      if (!normalizedGlobalSearch) return [];

      const tokens = normalizedGlobalSearch.split(" ").filter(Boolean);
      if (!tokens.length) return [];

      return safeList(globalSearchEntries)
         .filter((entry) =>
            tokens.every((token) => String(entry?.searchable ?? "").includes(token)),
         )
         .slice(0, 200);
   }, [globalSearchEntries, normalizedGlobalSearch]);

   const verificationTicketIssueCountMap = useMemo(() => {
      const map = new Map();

      for (const finding of verificationFindings) {
         const ticketId = toPositiveInt(finding?.ticketId);
         if (!ticketId) continue;
         map.set(ticketId, Number(map.get(ticketId) || 0) + 1);
      }

      return map;
   }, [verificationFindings]);

   const middlePanelBadgeValue = useMemo(() => {
      if (normalizedGlobalSearch) return globalSearchResults.length;
      if (verificationReady) return verificationFindings.length;
      return questionRows.length;
   }, [
      globalSearchResults.length,
      normalizedGlobalSearch,
      questionRows.length,
      verificationFindings.length,
      verificationReady,
   ]);

   const categoryMap = useMemo(() => {
      return new Map(
         safeList(categories)
            .map((category) => {
               const id = toPositiveInt(category?.id);
               return id ? [id, category] : null;
            })
            .filter(Boolean),
      );
   }, [categories]);

   const dirtyQuestionIds = useMemo(() => {
      const dirtySet = new Set();

      for (const question of questionRows) {
         const draft = questionDrafts[question.id];
         if (draft && isQuestionDirty(question, draft)) {
            dirtySet.add(question.id);
         }
      }

      return dirtySet;
   }, [questionDrafts, questionRows]);

   const selectedQuestionIndex = useMemo(() => {
      return questionRows.findIndex(
         (question) => Number(question.id) === Number(selectedQuestionId),
      );
   }, [questionRows, selectedQuestionId]);

   const selectedQuestion =
      selectedQuestionIndex >= 0 ? questionRows[selectedQuestionIndex] : null;

   const selectedQuestionDraft = useMemo(() => {
      if (!selectedQuestion) return null;
      return (
         questionDrafts[selectedQuestion.id] || createDraftFromQuestion(selectedQuestion)
      );
   }, [questionDrafts, selectedQuestion]);

   const selectedQuestionDirty = Boolean(
      selectedQuestion && dirtyQuestionIds.has(selectedQuestion.id),
   );

   const ticketDirty = useMemo(() => {
      const currentName = String(ticketDraftName ?? "").trim();
      const originalName = String(selectedTicketMeta?.name ?? "").trim();
      if (currentName !== originalName) return true;

      const currentIds = questionRows.map((question) => Number(question.id));
      const originalIds = ticketOriginalQuestionIds.map((id) => Number(id));

      return JSON.stringify(currentIds) !== JSON.stringify(originalIds);
   }, [questionRows, selectedTicketMeta?.name, ticketDraftName, ticketOriginalQuestionIds]);

   const selectedCategoryLabel = useMemo(() => {
      const categoryId = selectedQuestionDraft?.categoryId;
      const numericCategoryId = toPositiveInt(categoryId);
      if (!numericCategoryId) return "Fără categorie";

      const category = categoryMap.get(numericCategoryId);
      if (!category) return `Categoria #${numericCategoryId}`;

      return (
         firstNonEmpty(category?.nameRo, category?.nameRu) ||
         `Categoria #${numericCategoryId}`
      );
   }, [categoryMap, selectedQuestionDraft?.categoryId]);

   const previewExamQuestion = useMemo(() => {
      return buildExamPreviewQuestion(selectedQuestionDraft, previewLang);
   }, [previewLang, selectedQuestionDraft]);

   const previewImageOriginal = String(selectedQuestionDraft?.image ?? "").trim();
   const previewImageNormalized = useMemo(() => {
      return normalizeExamImagePath(selectedQuestionDraft?.image || "");
   }, [selectedQuestionDraft?.image]);
   const previewImageDisplay =
      previewExamQuestion?.image || NO_IMAGE_PLACEHOLDER_URL;
   const previewCorrectAnswer =
      normalizeCorrectAnswer(
         selectedQuestionDraft?.correctAnswer,
         safeList(selectedQuestionDraft?.answers).length,
      ) || null;

   const versionedPreviewImage = useCallback((src, key) => {
      if (!src) return "";
      return src + (src.includes("?") ? "&" : "?") + `v=${key}`;
   }, []);

   const handlePreviewImageError = useCallback((event) => {
      const image = event.currentTarget;
      const src = String(image.currentSrc || image.src || "");
      const normalizedSrc = src.split("?")[0];
      const isPlaceholder = normalizedSrc === NO_IMAGE_PLACEHOLDER_URL;

      if (!isPlaceholder) {
         image.src = NO_IMAGE_PLACEHOLDER_URL;
         return;
      }

      image.hidden = true;
   }, []);

   const updateDraftForQuestion = useCallback((questionId, updater) => {
      const normalizedId = toPositiveInt(questionId);
      if (!normalizedId) return;

      setQuestionDrafts((current) => {
         const question = questionRows.find(
            (item) => Number(item.id) === Number(normalizedId),
         );
         if (!question) return current;

         const existingDraft =
            current[normalizedId] || createDraftFromQuestion(question);
         const nextDraft =
            typeof updater === "function"
               ? updater({
                    ...existingDraft,
                    answers: cloneAnswers(existingDraft?.answers),
                 })
               : updater;

         return {
            ...current,
            [normalizedId]: nextDraft,
         };
      });
   }, [questionRows]);

   const selectRelativeQuestion = useCallback(
      (step) => {
         if (!questionRows.length) return;

         const currentIndex = questionRows.findIndex(
            (question) => Number(question.id) === Number(selectedQuestionId),
         );
         const safeIndex = currentIndex >= 0 ? currentIndex : 0;
         const nextIndex = Math.min(
            questionRows.length - 1,
            Math.max(0, safeIndex + Number(step || 0)),
         );

         setSelectedQuestionId(questionRows[nextIndex]?.id ?? null);
      },
      [questionRows, selectedQuestionId],
   );

   const handleMoveSelectedQuestion = useCallback(
      (direction) => {
         setQuestionRows((current) => {
            const currentIndex = current.findIndex(
               (question) => Number(question.id) === Number(selectedQuestionId),
            );
            if (currentIndex < 0) return current;

            const targetIndex = currentIndex + Number(direction || 0);
            if (targetIndex < 0 || targetIndex >= current.length) {
               return current;
            }

            const next = [...current];
            const [moved] = next.splice(currentIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next;
         });
      },
      [selectedQuestionId],
   );

   const handleResetQuestionDraft = useCallback(() => {
      if (!selectedQuestion?.id) return;

      setQuestionDrafts((current) => {
         const next = { ...current };
         delete next[selectedQuestion.id];
         return next;
      });
   }, [selectedQuestion?.id]);

   const handleResetTicketDraft = useCallback(() => {
      setTicketDraftName(String(selectedTicketMeta?.name ?? ""));
      setQuestionRows((current) => orderQuestionsByIds(current, ticketOriginalQuestionIds));
   }, [selectedTicketMeta?.name, ticketOriginalQuestionIds]);

   const handleSaveQuestion = useCallback(async () => {
      if (!selectedQuestion?.id || !selectedQuestionDraft) return;

      try {
         setQuestionSaving(true);
         const payload = buildQuestionPayloadFromDraft(selectedQuestionDraft);

         await updateExamQuestion(selectedQuestion.id, payload);

         const savedQuestion = applyDraftToQuestion(selectedQuestion, selectedQuestionDraft);

         setQuestionRows((current) =>
            current.map((question) =>
               Number(question.id) === Number(selectedQuestion.id)
                  ? savedQuestion
                  : question,
            ),
         );
         setQuestionDrafts((current) => {
            const next = { ...current };
            delete next[selectedQuestion.id];
            return next;
         });
         setGlobalSearchEntries((current) =>
            current.map((entry) => {
               if (Number(entry?.questionId) !== Number(savedQuestion.id)) {
                  return entry;
               }

               return (
                  buildGlobalQuestionSearchEntry(
                     {
                        id: entry.ticketId,
                        name: entry.ticketName,
                     },
                     savedQuestion,
                     entry.order,
                  ) || entry
               );
            }),
         );
         if (verificationReady) {
            setVerificationStale(true);
         }

         pushAlert("Întrebarea a fost actualizată.", "success");
      } catch (error) {
         pushAlert(
            error?.message || "Nu am putut salva întrebarea.",
            "error",
         );
      } finally {
         setQuestionSaving(false);
      }
   }, [pushAlert, selectedQuestion, selectedQuestionDraft, verificationReady]);

   const handleSaveTicket = useCallback(async () => {
      const ticketId = toPositiveInt(selectedTicketMeta?.id ?? selectedTicketId);
      if (!ticketId) return;

      try {
         setTicketSaving(true);

         const nextName =
            String(ticketDraftName ?? "").trim() ||
            String(selectedTicketMeta?.name ?? "").trim() ||
            `Bilet ${ticketId}`;
         const nextQuestionIds = uniqPositiveInts(
            questionRows.map((question) => question?.id),
         );

         if (!nextQuestionIds.length) {
            throw new Error("Biletul nu conține întrebări valide.");
         }

         await updateExamTicket(ticketId, {
            name: nextName,
            questionIds: nextQuestionIds,
         });

         setSelectedTicketMeta((current) =>
            current
               ? {
                    ...current,
                    name: nextName,
                    questionIds: nextQuestionIds,
                 }
               : current,
         );
         setTicketDraftName(nextName);
         setTicketOriginalQuestionIds(nextQuestionIds);
         setTicketRows((current) =>
            current.map((ticket) =>
               Number(ticket.id) === Number(ticketId)
                  ? {
                       ...ticket,
                       name: nextName,
                       questionCount: nextQuestionIds.length,
                    }
                  : ticket,
            ),
         );
         setGlobalSearchEntries((current) => {
            const others = current.filter(
               (entry) => Number(entry?.ticketId) !== Number(ticketId),
            );
            const rebuilt = questionRows
               .map((question, index) =>
                  buildGlobalQuestionSearchEntry(
                     {
                        id: ticketId,
                        name: nextName,
                     },
                     question,
                     index + 1,
                  ),
               )
               .filter(Boolean);

            return sortGlobalSearchEntries([...others, ...rebuilt]);
         });
         if (verificationReady) {
            setVerificationStale(true);
         }

         pushAlert("Biletul a fost actualizat.", "success");
      } catch (error) {
         pushAlert(error?.message || "Nu am putut salva biletul.", "error");
      } finally {
         setTicketSaving(false);
      }
   }, [
      pushAlert,
      questionRows,
      selectedTicketId,
      selectedTicketMeta?.id,
      selectedTicketMeta?.name,
      ticketDraftName,
      verificationReady,
   ]);

   const handleOpenGlobalSearchResult = useCallback(
      (result) => {
         const ticketId = toPositiveInt(result?.ticketId);
         const questionId = toPositiveInt(result?.questionId);
         if (!ticketId) return;

         pendingQuestionJumpRef.current = questionId
            ? { ticketId, questionId }
            : null;

         if (Number(ticketId) === Number(selectedTicketId)) {
            if (questionId) {
               setSelectedQuestionId(questionId);
               pendingQuestionJumpRef.current = null;
            }
            return;
         }

         setSelectedTicketId(ticketId);
      },
      [selectedTicketId],
   );

   return (
      <section className="exam-admin-page">
         <div className="exam-admin exam-correction">
            {alert ? (
               <div className="exam-admin__pager">
                  <span>
                     <strong>{String(alert.type || "info").toUpperCase()}</strong>:{" "}
                     {alert.message}
                  </span>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={() => setAlert(null)}
                  >
                     Închide
                  </button>
               </div>
            ) : null}

            <div className="exam-admin__topbar">
               <div className="exam-correction__hero">
                  <h2 className="exam-correction__title">Corectare Exam</h2>
                  <p className="exam-admin__muted">
                     Parcurgi biletele de tip examen, vezi imaginea și editezi
                     manual întrebările sau ordinea lor în bilet.
                  </p>
               </div>

               <div className="exam-admin__actions">
                  <Link to="/admin/bilete" className="exam-admin__action-btn">
                     Import exam
                  </Link>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={handleExportAllTickets}
                     disabled={
                        exportLoading ||
                        ticketLoading ||
                        selectedTicketLoading ||
                        verificationLoading ||
                        globalSearchLoading
                     }
                  >
                     {exportLoading ? "Se exportă JSON…" : "Exportă JSON bilete"}
                  </button>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={loadTickets}
                     disabled={
                        ticketLoading ||
                        selectedTicketLoading ||
                        verificationLoading ||
                        globalSearchLoading ||
                        exportLoading
                     }
                  >
                     Reîncarcă biletele
                  </button>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={() => loadSelectedTicket(selectedTicketId)}
                     disabled={
                        !selectedTicketId ||
                        selectedTicketLoading ||
                        verificationLoading ||
                        globalSearchLoading
                     }
                  >
                     Reîncarcă biletul curent
                  </button>
               </div>
            </div>

            {exportProgress ? (
               <p className="exam-admin__muted exam-correction__export-progress">
                  Export JSON: {exportProgress.current}/{exportProgress.total}
                  {exportProgress.ticketName
                     ? ` · ${exportProgress.ticketName}`
                     : ""}
               </p>
            ) : null}

            <article className="exam-admin__panel exam-correction__import-panel">
               <div className="exam-admin__panel-head">
                  <h3>Import bilete corectate</h3>
                  {importBundle?.summary ? (
                     <span className="exam-admin__badge">
                        {importBundle.summary.questionsTotal}
                     </span>
                  ) : null}
               </div>

               <div className="exam-correction__import-row">
                  <label className="exam-admin__primary-btn exam-correction__import-btn">
                     Alege JSON
                     <input
                        type="file"
                        accept="application/json,.json"
                        onChange={handleImportOfficialFile}
                        disabled={importApplying || exportLoading}
                     />
                  </label>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={handleApplyOfficialImport}
                     disabled={
                        !importBundle?.tickets?.length ||
                        importApplying ||
                        ticketLoading
                     }
                  >
                     {importApplying ? "Se aplică…" : "Aplică importul"}
                  </button>
               </div>

               <p className="exam-admin__muted">
                  {importFileName
                     ? `Fișier: ${importFileName} · ${importBundle?.summary?.ticketsTotal || 0} bilete · ${importBundle?.summary?.questionsTotal || 0} întrebări`
                     : "Format: bilete cu întrebări RO/RU, ordine și răspuns corect (ex. PRIMELE_6_BILETE…json)"}
               </p>
            </article>

            <div className="exam-admin__layout exam-admin__layout--questions">
               <article className="exam-admin__panel">
                  <div className="exam-admin__panel-head">
                     <h3>Bilete exam</h3>
                     <span className="exam-admin__badge">{ticketRows.length}</span>
                  </div>

                  <div className="exam-admin__toolbar">
                     <input
                        type="text"
                        className="exam-admin__input"
                        placeholder="Caută după ID sau nume"
                        value={ticketSearch}
                        onChange={(event) => setTicketSearch(event.target.value)}
                        disabled={ticketLoading}
                     />
                  </div>

                  <div className="exam-admin__ticket-list">
                     {filteredTickets.length ? (
                        filteredTickets.map((ticket) => {
                           const isActive =
                              Number(ticket.id) === Number(selectedTicketId);
                           const issueCount = Number(
                              verificationTicketIssueCountMap.get(
                                 Number(ticket.id),
                              ) || 0,
                           );

                           return (
                              <button
                                 key={ticket.localId}
                                 type="button"
                                 className={`exam-admin__ticket-item${
                                    isActive ? " exam-admin__ticket-item--active" : ""
                                 }`}
                                 onClick={() => setSelectedTicketId(ticket.id)}
                              >
                                 <div className="exam-admin__ticket-top">
                                    <strong>#{ticket.id}</strong>
                                    <div className="exam-correction__badges">
                                       <span className="exam-admin__badge">
                                          {ticket.questionCount || 0} întrebări
                                       </span>
                                       {verificationReady ? (
                                          <span
                                             className={`exam-admin__badge${
                                                issueCount
                                                   ? " exam-admin__badge--warn"
                                                   : ""
                                             }`}
                                          >
                                             {issueCount
                                                ? `${issueCount} probleme`
                                                : "OK"}
                                          </span>
                                       ) : null}
                                    </div>
                                 </div>
                                 <p>{ticket.name || "(fără nume)"}</p>
                                 <small>{ticket.type || "EXAM"}</small>
                              </button>
                           );
                        })
                     ) : (
                        <div className="exam-admin__preview-empty">
                           {ticketLoading
                              ? "Se încarcă biletele…"
                              : "Nu există bilete de tip exam."}
                        </div>
                     )}
                  </div>
               </article>

               <article className="exam-admin__panel">
                  <div className="exam-admin__panel-head">
                     <div>
                        <h3>Căutare + verificare</h3>
                        <p className="exam-admin__muted">
                           {selectedTicketMeta
                              ? `Bilet curent #${selectedTicketMeta.id}`
                              : "Selectează un bilet"}
                        </p>
                     </div>
                     <span
                        className={`exam-admin__badge${
                           !normalizedGlobalSearch &&
                           verificationReady &&
                           verificationFindings.length
                              ? " exam-admin__badge--warn"
                              : ""
                        }`}
                     >
                        {middlePanelBadgeValue}
                     </span>
                  </div>

                  {selectedTicketMeta ? (
                     <>
                        <label className="exam-admin__field">
                           <span>Nume bilet</span>
                           <input
                              type="text"
                              className="exam-admin__input"
                              value={ticketDraftName}
                              onChange={(event) => setTicketDraftName(event.target.value)}
                              disabled={selectedTicketLoading || ticketSaving}
                           />
                        </label>

                        <div className="exam-admin__actions">
                           <button
                              type="button"
                              className="exam-admin__primary-btn"
                              onClick={handleSaveTicket}
                              disabled={
                                 !ticketDirty ||
                                 ticketSaving ||
                                 questionSaving ||
                                 selectedTicketLoading
                              }
                           >
                              Salvează biletul
                           </button>
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={handleResetTicketDraft}
                              disabled={!ticketDirty || ticketSaving || selectedTicketLoading}
                           >
                              Resetează biletul
                           </button>
                        </div>

                        <label className="exam-admin__field">
                           <span>Caută în toate întrebările din toate biletele</span>
                           <input
                              type="text"
                              className="exam-admin__input"
                              placeholder="Text întrebare, răspuns, ID bilet sau ID întrebare"
                              value={globalQuestionSearch}
                              onChange={(event) =>
                                 setGlobalQuestionSearch(event.target.value)
                              }
                           />
                        </label>

                        <div className="exam-admin__actions">
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={() => {
                                 setGlobalSearchReady(false);
                                 setGlobalSearchEntries([]);
                                 setGlobalSearchError("");
                                 ensureGlobalSearchIndex(true);
                              }}
                              disabled={globalSearchLoading}
                           >
                              {globalSearchLoading
                                 ? "Indexare..."
                                 : globalSearchReady
                                   ? "Reindexează căutarea"
                                   : "Pregătește căutarea"}
                           </button>
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={runSelectedTicketVerification}
                              disabled={
                                 !selectedTicketId ||
                                 verificationLoading ||
                                 globalSearchLoading ||
                                 selectedTicketLoading ||
                                 ticketSaving ||
                                 questionSaving
                              }
                           >
                              {verificationLoading
                                 ? "Verific..."
                                 : "Verifică biletul curent"}
                           </button>
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={runVerificationScan}
                              disabled={
                                 verificationLoading ||
                                 globalSearchLoading ||
                                 ticketSaving ||
                                 questionSaving
                              }
                           >
                              {verificationLoading
                                 ? "Verific..."
                                 : verificationReady
                                   ? "Reverifică toate biletele"
                                   : "Verifică toate biletele"}
                           </button>
                           {normalizedGlobalSearch ? (
                              <button
                                 type="button"
                                 className="exam-admin__action-btn"
                                 onClick={() => setGlobalQuestionSearch("")}
                              >
                                 Golește căutarea
                              </button>
                           ) : null}
                           {verificationReady ? (
                              <button
                                 type="button"
                                 className="exam-admin__action-btn"
                                 onClick={resetVerificationReport}
                                 disabled={verificationLoading}
                              >
                                 Golește raportul
                              </button>
                           ) : null}
                        </div>

                        {verificationReady || verificationLoading || verificationError ? (
                           <>
                              <div className="exam-admin__stats-grid">
                                 <div className="exam-admin__stat">
                                    <div className="exam-admin__stat-value">
                                       {verificationSummary.ticketsScanned}
                                    </div>
                                    <div className="exam-admin__stat-label">
                                       Bilete verificate
                                    </div>
                                 </div>
                                 <div className="exam-admin__stat">
                                    <div className="exam-admin__stat-value">
                                       {verificationSummary.questionsScanned}
                                    </div>
                                    <div className="exam-admin__stat-label">
                                       Întrebări verificate
                                    </div>
                                 </div>
                                 <div
                                    className={`exam-admin__stat${
                                       verificationSummary.ticketsWithIssues
                                          ? " exam-admin__stat--warn"
                                          : ""
                                    }`}
                                 >
                                    <div className="exam-admin__stat-value">
                                       {verificationSummary.ticketsWithIssues}
                                    </div>
                                    <div className="exam-admin__stat-label">
                                       Bilete cu probleme
                                    </div>
                                 </div>
                                 <div
                                    className={`exam-admin__stat${
                                       verificationSummary.questionsWithIssues
                                          ? " exam-admin__stat--warn"
                                          : ""
                                    }`}
                                 >
                                    <div className="exam-admin__stat-value">
                                       {verificationSummary.questionsWithIssues}
                                    </div>
                                    <div className="exam-admin__stat-label">
                                       Întrebări afectate
                                    </div>
                                 </div>
                                 <div
                                    className={`exam-admin__stat${
                                       verificationSummary.issuesCount
                                          ? " exam-admin__stat--warn"
                                          : ""
                                    }`}
                                 >
                                    <div className="exam-admin__stat-value">
                                       {verificationSummary.issuesCount}
                                    </div>
                                    <div className="exam-admin__stat-label">
                                       Probleme totale
                                    </div>
                                 </div>
                              </div>

                              {verificationStale ? (
                                 <p className="exam-admin__muted">
                                    Raportul de verificare poate fi depășit după
                                    salvările recente. Rulează din nou verificarea.
                                 </p>
                              ) : null}
                           </>
                        ) : null}

                        <div className="exam-admin__list">
                           {normalizedGlobalSearch ? (
                              globalSearchLoading && !globalSearchReady ? (
                                 <div className="exam-admin__preview-empty">
                                    Se indexează toate întrebările pentru căutare…
                                 </div>
                              ) : globalSearchError ? (
                                 <div className="exam-admin__preview-empty">
                                    {globalSearchError}
                                 </div>
                              ) : globalSearchResults.length ? (
                                 globalSearchResults.map((result) => {
                                    const isActive =
                                       Number(result.ticketId) === Number(selectedTicketId) &&
                                       Number(result.questionId) ===
                                          Number(selectedQuestionId);
                                    const category = categoryMap.get(
                                       Number(result.categoryId),
                                    );

                                    return (
                                       <button
                                          key={result.localId}
                                          type="button"
                                          className={`exam-admin__list-item${
                                             isActive
                                                ? " exam-admin__list-item--active"
                                                : ""
                                          }`}
                                          onClick={() =>
                                             handleOpenGlobalSearchResult(result)
                                          }
                                       >
                                          <div className="exam-admin__list-item-top">
                                             <strong>
                                                Bilet {result.ticketId} • Întrebarea{" "}
                                                {result.order}
                                             </strong>
                                             <div className="exam-correction__badges">
                                                {result.image ? (
                                                   <span className="exam-admin__badge">
                                                      Imagine
                                                   </span>
                                                ) : null}
                                                {Number(result.ticketId) ===
                                                   Number(selectedTicketId) &&
                                                Number(result.questionId) ===
                                                   Number(selectedQuestionId) ? (
                                                   <span className="exam-admin__badge">
                                                      Deschisă
                                                   </span>
                                                ) : null}
                                             </div>
                                          </div>
                                          <p>
                                             {truncateText(
                                                firstNonEmpty(
                                                   result.textRo,
                                                   result.textRu,
                                                   "(fără text)",
                                                ),
                                                125,
                                             )}
                                          </p>
                                          <small>
                                             {result.ticketName} • ID întrebare{" "}
                                             {result.questionId}
                                             {category
                                                ? ` • ${firstNonEmpty(
                                                     category?.nameRo,
                                                     category?.nameRu,
                                                  )}`
                                                : ""}
                                          </small>
                                       </button>
                                    );
                                 })
                              ) : (
                                 <div className="exam-admin__preview-empty">
                                    Nu am găsit nimic pentru această căutare.
                                 </div>
                              )
                           ) : verificationLoading ? (
                              <div className="exam-admin__preview-empty">
                                 Verific toate biletele și toate întrebările pentru
                                 text lipsă, răspunsuri lipsă și răspuns corect lipsă…
                              </div>
                           ) : verificationError ? (
                              <div className="exam-admin__preview-empty">
                                 {verificationError}
                              </div>
                           ) : verificationReady ? (
                              verificationFindings.length ? (
                                 verificationFindings.map((finding) => {
                                    const isActiveTicket =
                                       Number(finding.ticketId) ===
                                       Number(selectedTicketId);
                                    const isActiveQuestion =
                                       !finding.questionId ||
                                       Number(finding.questionId) ===
                                          Number(selectedQuestionId);

                                    return (
                                       <button
                                          key={finding.localId}
                                          type="button"
                                          className={`exam-admin__list-item exam-admin__list-item--warn${
                                             isActiveTicket && isActiveQuestion
                                                ? " exam-admin__list-item--active"
                                                : ""
                                          }`}
                                          onClick={() =>
                                             handleOpenGlobalSearchResult(finding)
                                          }
                                       >
                                          <div className="exam-admin__list-item-top">
                                             <strong>
                                                {finding.questionId
                                                   ? `Bilet ${finding.ticketId} • Întrebarea ${
                                                        finding.order || "?"
                                                     }`
                                                   : `Bilet ${finding.ticketId}`}
                                             </strong>
                                             <div className="exam-correction__badges">
                                                <span className="exam-admin__badge exam-admin__badge--warn">
                                                   {finding.shortLabel}
                                                </span>
                                                {isActiveTicket && isActiveQuestion ? (
                                                   <span className="exam-admin__badge">
                                                      Deschis
                                                   </span>
                                                ) : null}
                                             </div>
                                          </div>
                                          <p>{finding.message}</p>
                                          <small>
                                             {finding.ticketName}
                                             {finding.questionId
                                                ? ` • ID întrebare ${finding.questionId}`
                                                : ""}
                                             {finding.previewText
                                                ? ` • ${truncateText(
                                                     finding.previewText,
                                                     90,
                                                  )}`
                                                : ""}
                                          </small>
                                       </button>
                                    );
                                 })
                              ) : (
                                 <div className="exam-admin__preview-empty">
                                    Verificare completă. Nu am găsit câmpuri lipsă în
                                    biletele scanate.
                                 </div>
                              )
                           ) : selectedTicketLoading ? (
                              <div className="exam-admin__preview-empty">
                                 Se încarcă biletul curent…
                              </div>
                           ) : (
                              <div className="exam-admin__preview-empty">
                                 Scrie în search ca să cauți global sau apasă pe
                                 verificare ca să vezi rapid câmpurile lipsă din
                                 biletul curent sau din toate biletele.
                              </div>
                           )}
                        </div>
                     </>
                  ) : (
                     <div className="exam-admin__preview-empty">
                        Selectează un bilet din stânga.
                     </div>
                  )}
               </article>

               <article className="exam-admin__panel exam-admin__panel--preview">
                  <div className="exam-admin__panel-head">
                     <div>
                        <h3>Editor întrebare</h3>
                        <p className="exam-admin__muted">
                           {selectedQuestion
                              ? `Întrebarea ID ${selectedQuestion.id}`
                              : "Alege o întrebare"}
                        </p>
                     </div>

                     {selectedQuestion ? (
                        <span
                           className={`exam-admin__badge${
                              selectedQuestionDirty ? " exam-admin__badge--warn" : ""
                           }`}
                        >
                           {selectedQuestionDirty ? "Modificată" : "Salvată"}
                        </span>
                     ) : null}
                  </div>

                  {selectedQuestion && selectedQuestionDraft ? (
                     <div className="exam-correction__editor">
                        <div className="exam-admin__actions">
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={() => handleMoveSelectedQuestion(-1)}
                              disabled={selectedQuestionIndex <= 0 || ticketSaving}
                           >
                              Mută sus
                           </button>
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={() => handleMoveSelectedQuestion(1)}
                              disabled={
                                 selectedQuestionIndex < 0 ||
                                 selectedQuestionIndex >= questionRows.length - 1 ||
                                 ticketSaving
                              }
                           >
                              Mută jos
                           </button>
                           <button
                              type="button"
                              className="exam-admin__primary-btn"
                              onClick={handleSaveQuestion}
                              disabled={questionSaving || selectedTicketLoading}
                           >
                              Salvează întrebarea
                           </button>
                           <button
                              type="button"
                              className="exam-admin__action-btn"
                              onClick={handleResetQuestionDraft}
                              disabled={!selectedQuestionDirty || questionSaving}
                           >
                              Resetează întrebarea
                           </button>
                        </div>

                        <label className="exam-admin__field">
                           <span>Text întrebare RO</span>
                           <textarea
                              className="exam-admin__textarea"
                              rows={5}
                              value={selectedQuestionDraft.textRo}
                              onChange={(event) =>
                                 updateDraftForQuestion(selectedQuestion.id, (draft) => ({
                                    ...draft,
                                    textRo: event.target.value,
                                 }))
                              }
                           />
                        </label>

                        <label className="exam-admin__field">
                           <span>Text întrebare RU</span>
                           <textarea
                              className="exam-admin__textarea"
                              rows={5}
                              value={selectedQuestionDraft.textRu}
                              onChange={(event) =>
                                 updateDraftForQuestion(selectedQuestion.id, (draft) => ({
                                    ...draft,
                                    textRu: event.target.value,
                                 }))
                              }
                           />
                        </label>

                        <div className="exam-correction__split">
                           <label className="exam-admin__field">
                              <span>Link imagine</span>
                              <input
                                 type="text"
                                 className="exam-admin__input"
                                 value={selectedQuestionDraft.image}
                                 onChange={(event) =>
                                    updateDraftForQuestion(selectedQuestion.id, (draft) => ({
                                       ...draft,
                                       image: event.target.value,
                                    }))
                                 }
                              />
                           </label>

                           <label className="exam-admin__field">
                              <span>Categorie</span>
                              <select
                                 className="exam-admin__select"
                                 value={selectedQuestionDraft.categoryId}
                                 onChange={(event) =>
                                    updateDraftForQuestion(selectedQuestion.id, (draft) => ({
                                       ...draft,
                                       categoryId: event.target.value,
                                    }))
                                 }
                              >
                                 <option value="">Fără categorie</option>
                                 {safeList(categories).map((category) => {
                                    const categoryId = toPositiveInt(category?.id);
                                    if (!categoryId) return null;

                                    const label =
                                       firstNonEmpty(
                                          category?.nameRo,
                                          category?.nameRu,
                                       ) || `Categoria #${categoryId}`;

                                    return (
                                       <option key={categoryId} value={String(categoryId)}>
                                          {label}
                                       </option>
                                    );
                                 })}
                              </select>
                           </label>
                        </div>

                        <div className="exam-admin__field">
                           <div className="exam-admin__panel-head">
                              <span className="exam-admin__field-title">
                                 Răspunsuri
                              </span>
                              <button
                                 type="button"
                                 className="exam-admin__action-btn"
                                 onClick={() =>
                                    updateDraftForQuestion(selectedQuestion.id, (draft) => {
                                       if (safeList(draft?.answers).length >= MAX_ANSWERS) {
                                          return draft;
                                       }

                                       return {
                                          ...draft,
                                          answers: [
                                             ...cloneAnswers(draft?.answers),
                                             { ro: "", ru: "" },
                                          ],
                                       };
                                    })
                                 }
                                 disabled={
                                    safeList(selectedQuestionDraft?.answers).length >=
                                    MAX_ANSWERS
                                 }
                              >
                                 Adaugă răspuns
                              </button>
                           </div>

                           <div className="exam-admin__answers">
                              {safeList(selectedQuestionDraft.answers).map(
                                 (answer, answerIndex) => (
                                    <div
                                       key={`${selectedQuestion.id}-answer-${answerIndex}`}
                                       className="exam-admin__answer-row exam-correction__answer-row"
                                    >
                                       <button
                                          type="button"
                                          className={`exam-admin__correct-toggle${
                                             Number(selectedQuestionDraft.correctAnswer) ===
                                             answerIndex + 1
                                                ? " exam-admin__correct-toggle--active"
                                                : ""
                                          }`}
                                          onClick={() =>
                                             updateDraftForQuestion(
                                                selectedQuestion.id,
                                                (draft) => ({
                                                   ...draft,
                                                   correctAnswer: answerIndex + 1,
                                                }),
                                             )
                                          }
                                          title="Marchează răspunsul corect"
                                       >
                                          {answerIndex + 1}
                                       </button>

                                       <div className="exam-correction__answer-fields">
                                          <input
                                             type="text"
                                             className="exam-admin__input"
                                             placeholder={`Răspuns ${answerIndex + 1} RO`}
                                             value={answer.ro}
                                             onChange={(event) =>
                                                updateDraftForQuestion(
                                                   selectedQuestion.id,
                                                   (draft) => {
                                                      const answers = cloneAnswers(
                                                         draft?.answers,
                                                      );
                                                      answers[answerIndex] = {
                                                         ...answers[answerIndex],
                                                         ro: event.target.value,
                                                      };

                                                      return {
                                                         ...draft,
                                                         answers,
                                                      };
                                                   },
                                                )
                                             }
                                          />
                                          <input
                                             type="text"
                                             className="exam-admin__input"
                                             placeholder={`Răspuns ${answerIndex + 1} RU`}
                                             value={answer.ru}
                                             onChange={(event) =>
                                                updateDraftForQuestion(
                                                   selectedQuestion.id,
                                                   (draft) => {
                                                      const answers = cloneAnswers(
                                                         draft?.answers,
                                                      );
                                                      answers[answerIndex] = {
                                                         ...answers[answerIndex],
                                                         ru: event.target.value,
                                                      };

                                                      return {
                                                         ...draft,
                                                         answers,
                                                      };
                                                   },
                                                )
                                             }
                                          />
                                       </div>

                                       <button
                                          type="button"
                                          className="exam-admin__danger-btn"
                                          onClick={() =>
                                             updateDraftForQuestion(
                                                selectedQuestion.id,
                                                (draft) => {
                                                   const answers = cloneAnswers(
                                                      draft?.answers,
                                                   );
                                                   if (answers.length <= MIN_ANSWERS) {
                                                      return draft;
                                                   }

                                                   answers.splice(answerIndex, 1);

                                                   const maxCorrect = Math.max(
                                                      1,
                                                      answers.length,
                                                   );
                                                   let nextCorrect = Number(
                                                      draft?.correctAnswer ?? 1,
                                                   );

                                                   if (nextCorrect === answerIndex + 1) {
                                                      nextCorrect = Math.min(
                                                         answerIndex + 1,
                                                         maxCorrect,
                                                      );
                                                   } else if (nextCorrect > answerIndex + 1) {
                                                      nextCorrect -= 1;
                                                   }

                                                   return {
                                                      ...draft,
                                                      answers,
                                                      correctAnswer: Math.max(
                                                         1,
                                                         Math.min(
                                                            maxCorrect,
                                                            nextCorrect,
                                                         ),
                                                      ),
                                                   };
                                                },
                                             )
                                          }
                                          disabled={
                                             safeList(selectedQuestionDraft?.answers).length <=
                                             MIN_ANSWERS
                                          }
                                       >
                                          Șterge
                                       </button>
                                    </div>
                                 ),
                              )}
                           </div>
                        </div>

                        <div className="exam-correction__preview-topbar">
                           <strong>Preview Exam.jsx</strong>

                           <div className="exam-admin__lang-switch">
                              <button
                                 type="button"
                                 className={`exam-admin__lang-btn${
                                    previewLang === "ro"
                                       ? " exam-admin__lang-btn--active"
                                       : ""
                                 }`}
                                 onClick={() => setPreviewLang("ro")}
                              >
                                 RO
                              </button>
                              <button
                                 type="button"
                                 className={`exam-admin__lang-btn${
                                    previewLang === "ru"
                                       ? " exam-admin__lang-btn--active"
                                       : ""
                                 }`}
                                 onClick={() => setPreviewLang("ru")}
                              >
                                 RU
                              </button>
                           </div>
                        </div>

                        <div className="exam-admin__preview">
                           <div className="practice exam exam-correction__exam-preview">
                              <div className="practice__toolbar">
                                 <div className="practice__toolbar-center">
                                    <div className="practice__question-index">
                                       Întrebarea {Math.max(1, selectedQuestionIndex + 1)}/
                                       {Math.max(1, questionRows.length)}
                                    </div>
                                 </div>

                                 <div className="practice__timer">
                                    Corect: {previewCorrectAnswer || "-"}
                                 </div>
                              </div>

                              <div className="practice__statusboard">
                                 {questionRows.map((question, index) => (
                                    <button
                                       key={`preview-dot-${question.id}`}
                                       type="button"
                                       className={
                                          "practice__dot" +
                                          (Number(question.id) ===
                                          Number(selectedQuestionId)
                                             ? " practice__dot--current"
                                             : " practice__dot--none")
                                       }
                                       title={`Întrebarea ${index + 1}`}
                                       onClick={() => setSelectedQuestionId(question.id)}
                                    >
                                       {index + 1}
                                    </button>
                                 ))}
                              </div>

                              <div className="practice__question">
                                 <div className="practice__qtext">
                                    {previewExamQuestion?.text}
                                 </div>

                                 <div className="practice__row">
                                    <div className="practice__qimage-wrapper">
                                       <img
                                          key={`preview-image-${
                                             selectedQuestion.id
                                          }-${previewLang}-${previewImageDisplay}`}
                                          className="practice__qimage"
                                          src={versionedPreviewImage(
                                             previewImageDisplay,
                                             `${selectedQuestion.id}-${previewLang}-${previewImageDisplay}`,
                                          )}
                                          alt={`Întrebarea ${selectedQuestionIndex + 1}`}
                                          onError={handlePreviewImageError}
                                       />
                                    </div>

                                    <div className="practice__qimage-wrapper mobile">
                                       <img
                                          key={`preview-image-mobile-${
                                             selectedQuestion.id
                                          }-${previewLang}-${previewImageDisplay}`}
                                          className="practice__qimage"
                                          src={versionedPreviewImage(
                                             previewImageDisplay,
                                             `${selectedQuestion.id}-${previewLang}-${previewImageDisplay}-m`,
                                          )}
                                          alt={`Întrebarea ${selectedQuestionIndex + 1}`}
                                          onError={handlePreviewImageError}
                                       />
                                    </div>

                                    <div className="practice__answers">
                                       {safeList(previewExamQuestion?.answers).map(
                                          (answer, answerIndex) => {
                                             const isCorrectOption =
                                                Number.isInteger(
                                                   previewExamQuestion?.correctIndex,
                                                ) &&
                                                answerIndex ===
                                                   Number(
                                                      previewExamQuestion?.correctIndex,
                                                   );

                                             const className =
                                                "practice__answer" +
                                                (isCorrectOption
                                                   ? " practice__answer--correct"
                                                   : "") +
                                                " practice__answer--locked";

                                             return (
                                                <button
                                                   key={`preview-answer-${answerIndex}`}
                                                   type="button"
                                                   className={className}
                                                   disabled
                                                   title={
                                                      isCorrectOption
                                                         ? "Răspuns corect"
                                                         : "Răspuns"
                                                   }
                                                >
                                                   <span>{answer}</span>
                                                </button>
                                             );
                                          },
                                       )}
                                    </div>
                                 </div>

                                 <div className="practice__actions">
                                    <button
                                       type="button"
                                       className="practice__back bottom"
                                       onClick={() => selectRelativeQuestion(-1)}
                                       disabled={selectedQuestionIndex <= 0}
                                    >
                                       Înapoi
                                    </button>
                                    <div className="practice__spacer" />
                                    <button
                                       type="button"
                                       className="practice__secondary practice__secondary--primary"
                                       onClick={() => selectRelativeQuestion(1)}
                                       disabled={
                                          selectedQuestionIndex >=
                                          questionRows.length - 1
                                       }
                                    >
                                       Următorul
                                    </button>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <div className="exam-correction__preview-meta">
                           <div className="exam-correction__preview-meta-item">
                              <span className="exam-admin__field-title">
                                 Categorie
                              </span>
                              <p>{selectedCategoryLabel}</p>
                           </div>

                           <div className="exam-correction__preview-meta-item">
                              <span className="exam-admin__field-title">
                                 Imagine originală
                              </span>
                              <p>{previewImageOriginal || "Fără imagine"}</p>
                           </div>

                           <div className="exam-correction__preview-meta-item">
                              <span className="exam-admin__field-title">
                                 Imagine folosită în Exam.jsx
                              </span>
                              <p>
                                 {rewriteImageUrl(previewImageNormalized || "") ||
                                    NO_IMAGE_PLACEHOLDER_URL}
                              </p>
                           </div>
                        </div>
                     </div>
                  ) : (
                     <div className="exam-admin__preview-empty">
                        Selectează o întrebare din coloana din mijloc.
                     </div>
                  )}
               </article>
            </div>
         </div>
      </section>
   );
}
